/**
 * Book Registry - Auto-discovers books from content/books/
 * 
 * This file provides a centralized registry that:
 * 1. Auto-imports all book configs from content/books/
 * 2. Provides helper functions for accessing book data
 * 3. Supports dynamic route generation
 * 
 * To add a new book:
 * 1. Create folder in src/content/books/[book-name]/
 * 2. Add config.js and chapters.js
 * 3. Export from content/books/index.js
 * That's it! The book will be auto-discovered.
 */

import { allBooks, getConfigById, getChaptersById, getBookIds as getContentBookIds } from '../content/books';
import { allTextbooks, getTextbookConfigById, getTextbookChaptersById, getTextbookIds } from '../content/textbooks';

let bookConfigsCache = null;
let allContentCache = null;

const getAllContent = () => {
  if (allContentCache) {
    return allContentCache;
  }
  allContentCache = {
    ...allBooks,
    ...allTextbooks
  };
  return allContentCache;
};

const getBookConfigs = () => {
  if (bookConfigsCache) {
    return bookConfigsCache;
  }
  bookConfigsCache = Object.entries(getAllContent()).reduce((acc, [bookId, bookModule]) => {
    acc[bookId] = bookModule.config;
    return acc;
  }, {});
  return bookConfigsCache;
};

/**
 * Get configuration for a specific book
 * @param {string} bookId - The book identifier (e.g., 'SongOfRoland')
 * @returns {object|null} Book configuration or null if not found
 */
export function getBookConfig(bookId) {
  return getConfigById(bookId) || getTextbookConfigById(bookId);
}

/**
 * Get chapters for a specific book
 * @param {string} bookId - The book identifier
 * @returns {array} Array of chapters or empty array if not found
 */
export function getBookChapters(bookId) {
  return getChaptersById(bookId).length ? getChaptersById(bookId) : getTextbookChaptersById(bookId);
}

/**
 * Get all book configurations
 * @returns {object} All book configurations
 */
export function getAllBookConfigs() {
  return getBookConfigs();
}

/**
 * Get list of all book IDs
 * @returns {string[]} Array of book IDs
 */
export function getBookIds() {
  return [...getContentBookIds(), ...getTextbookIds()];
}

/**
 * Find book by route path
 * @param {string} route - The route path (e.g., 'Roland')
 * @returns {object|null} Book configuration or null if not found
 */
export function getBookByRoute(route) {
  return Object.values(getBookConfigs()).find(book => book.route === route) || null;
}

/**
 * Get books that use built-in reading pages (not external links)
 * @returns {object[]} Array of book configurations
 */
export function getBooksWithBuiltInContent() {
  return Object.values(getBookConfigs()).filter(book => !book.usesExternalLinks);
}

/**
 * Get books that use external links
 * @returns {object[]} Array of book configurations
 */
export function getBooksWithExternalLinks() {
  return Object.values(getBookConfigs()).filter(book => book.usesExternalLinks);
}

export default getBookConfigs;
